import React from "react";
import i18n from "../../../../../i18n/i18n";
import {Api} from "../../../../../helpers/api";
import {OrderService} from "../../../../services/orderService";
import {storesContext} from "../../../../../common/stores/stores";
import Button from "../../../../../common/components/shared/button/button";
import ReactPDF, {pdf} from "@react-pdf/renderer";
import {saveAs} from "file-saver";
import ExportOrderCargoFile from "../../orderDetails/pdfFiles/exportOrderCargoFile";
import ImportOrderCargoFile from "../../orderDetails/pdfFiles/importOrderCargoFile";
import ImportTrailerOrContainerOrderFile from "../../orderDetails/pdfFiles/importOrderTrailerOrContainerFile";
import ExportOrderTrailerOrContainerFile from "../../orderDetails/pdfFiles/exportOrderTrailerOrContainerFile";
import NoSeaTrafficOrderTrailerOrContainerFile from "../../orderDetails/pdfFiles/noSeaTrafficOrderTrailerOrContainerFile";
import {OrderCargoFileModel, OrderModel, TrailerOrContainerOrderFileModel} from "../../../../models/order";
import {MenuItem} from "@mui/material";
import {IUserStore} from "../../../../../administration/stores/userStore";
import {OrderFilesUtils} from "../../../../../helpers/orderFilesUtils";
import NoSeaTrafficOrderCargoFile from "../../orderDetails/pdfFiles/noSeaTrafficOrderCargoFile";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../../common/models/httpError";
import TransportUtils from "../../../../../helpers/transportUtils";

interface DownloadPrintButtonProps {
    orderId: string | null;
    orderData: OrderModel | undefined;
    isFileDownloadError: (response: AxiosResponse<HttpErrorModel[] | any> | undefined) => void;
}

interface DownloadPrintButtonState {
    orderCargoFileData: OrderCargoFileModel | null;
    trailerOrContainerOrderFileData: TrailerOrContainerOrderFileModel | null;
    renderButton: "none" | "cargo" | "trailerOrContainer";
    fileResponse:  AxiosResponse<HttpErrorModel[] | any> | undefined;
}

export default class DownloadPrintButton extends React.Component <DownloadPrintButtonProps, DownloadPrintButtonState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            orderCargoFileData: null,
            trailerOrContainerOrderFileData: null,
            renderButton: "none",
            fileResponse: undefined
        };
    }

    async componentDidMount() {
        if (!this.props.orderData || !this.props.orderId) return null;
        const userStore = this.context.userStore as IUserStore;

        if (userStore.canPrintOrderFile) {
            this.setState({renderButton: OrderFilesUtils.getOrderPrintButtonType(this.props.orderData.cargoType)});
        }
    }

    public render(): JSX.Element {
        return this.renderContent();
    }

    private renderContent(): JSX.Element {
        switch (this.state.renderButton) {
            case "cargo":
                return this.renderButtonDownloadForCargo();
            case "trailerOrContainer":
                return this.renderButtonDownloadForTrailerOrContainer();
            default:
                return <></>;
        }
    }

    private renderExportCargoButton(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForCargo();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <ExportOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.exportOrderCargoFileName", {number: this.state.orderCargoFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "export-order-cargo-btn");
    }

    private renderImportCargoButton(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForCargo();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <ImportOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.importOrderCargoFileName", {number: this.state.orderCargoFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "import-order-cargo-btn");
    }

    private renderPrintFileButtonForNoSeaTrafficCargo(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForCargo();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <NoSeaTrafficOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.noSeaTrafficCargoFileName", {number: this.state.orderCargoFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "no-sea-traffic-order-cargo-btn");
    }

    private renderButtonDownloadForCargo(): JSX.Element {
        if (this.isExportRelationType) {
            return this.renderExportCargoButton();
        } else if (this.isImportRelationType) {
            return this.renderImportCargoButton();
        } else if (this.isNoSeaTrafficRelationType) {
            return this.renderPrintFileButtonForNoSeaTrafficCargo();
        } else {
            return <></>;
        }
    }

    private renderExportTrailerOrContainerButton(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForTrailerOrContainer();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <ExportOrderTrailerOrContainerFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.exportTrailerOrContainerOrderFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "export-trailer-or-container-order-btn");
    }

    private renderImportTrailerOrContainerButton(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForTrailerOrContainer();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <ImportTrailerOrContainerOrderFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.importTrailerOrContainerOrderFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "import-trailer-or-container-order-btn");
    }

    private renderNoSeaTrafficTrailerOrContainerButton(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return this.renderPrintDownloadButton(async () => {
            await this.printFileDataForTrailerOrContainer();

            if (Api.isSuccess(this.state.fileResponse)) {
                this.downloadPrintFile(
                    <NoSeaTrafficOrderTrailerOrContainerFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>,
                    i18n.t("transportation.order.details.files.noSeaTrafficTrailerOrContainerFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})
                );
            } else {
                this.props.isFileDownloadError(this.state.fileResponse);
            }
        }, "no-sea-traffic-trailer-or-container-order-btn");
    }

    private renderButtonDownloadForTrailerOrContainer(): JSX.Element {
        if (this.isExportRelationType) {
            return this.renderExportTrailerOrContainerButton();
        } else if (this.isImportRelationType) {
            return this.renderImportTrailerOrContainerButton();
        } else if (this.isNoSeaTrafficRelationType) {
            return this.renderNoSeaTrafficTrailerOrContainerButton();
        } else {
            return <></>;
        }
    }

    private renderPrintDownloadButton(onClick: any, testId: string) {
        return (
            <MenuItem className="menu-item-wrapper">
                <Button
                    text={i18n.t("printToFile")}
                    type="secondary"
                    variant="text"
                    className="menu-item button-item"
                    onClick={onClick}
                    testId={testId}
                />
            </MenuItem>
        );
    }

    private async downloadPrintFile(doc: React.ReactElement<ReactPDF.DocumentProps, string | React.JSXElementConstructor<any>>, filename: string) {
        if (!doc || !filename) return null;

        const pdfFile = pdf(doc);
        const blob = await pdfFile.toBlob();

        saveAs(blob, filename);
    }

    private async printFileDataForCargo() {
        this.setState({fileResponse: undefined});

        const responseOrderCargoFile = await OrderService.getOrderCargoDataForFile(this.props.orderId!);

        this.setState({
            orderCargoFileData: Api.isSuccess(responseOrderCargoFile) ? responseOrderCargoFile?.data : null,
            fileResponse: responseOrderCargoFile
        });
    }

    private async printFileDataForTrailerOrContainer() {
        this.setState({fileResponse: undefined});

        const responseTrailerOrContainerOrderFile = await OrderService.getTrailerOrContainerOrderDataForFile(this.props.orderId!);

        this.setState({
            trailerOrContainerOrderFileData: Api.isSuccess(responseTrailerOrContainerOrderFile) ? responseTrailerOrContainerOrderFile?.data : null,
            fileResponse: responseTrailerOrContainerOrderFile
        });
    }

    private get isExportRelationType(): boolean {
        return OrderFilesUtils.isExportRelationType(this.props.orderData?.relationType || undefined);
    }

    private get isImportRelationType(): boolean {
        return OrderFilesUtils.isImportRelationType(this.props.orderData?.relationType || undefined);
    }

    private get isNoSeaTrafficRelationType(): boolean {
        return TransportUtils.isNoSeaTrafficRelationType(this.props.orderData?.relationType || undefined);
    }
}
